// Repeatable contact person / email / phone block shared by the client and vendor registration forms.
// The backend stores these in client_contacts / vendor_contacts; the first one is the primary contact.
import React from 'react';
import { Box, Button, Chip, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export const EMPTY_CONTACT = { contactPerson: '', email: '', phone: '' };

// Same rules as oms.utility.ContactDetails on the backend
const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isBlankContact = (c) => !c.contactPerson?.trim() && !c.email?.trim() && !c.phone?.trim();

/**
 * Form rows for a saved client/vendor; falls back to the legacy single contact for old records.
 * `savedPhone` remembers the number on file: older records can hold landlines or "+91 ..." numbers,
 * which stay valid until someone changes them (the backend applies the same rule).
 */
export const toContactForm = (contacts, legacyPerson, legacyPhone) => {
  if (contacts?.length) {
    return contacts.map((c) => ({
      contactPerson: c.contactPerson || '',
      email: c.email || '',
      phone: c.phone || '',
      savedPhone: c.phone || '',
    }));
  }
  if (legacyPerson || legacyPhone) {
    return [{ ...EMPTY_CONTACT, contactPerson: legacyPerson || '', phone: legacyPhone || '', savedPhone: legacyPhone || '' }];
  }
  return [{ ...EMPTY_CONTACT }];
};

// A loaded contact whose phone hasn't been touched (new rows have no savedPhone)
const isPhoneUnchanged = (c) => c.savedPhone !== undefined && (c.phone || '').trim() === c.savedPhone.trim();

/** Per-field format errors (required-ness is shown with the asterisk and blocks the submit button). */
export const contactErrors = (c) => {
  const errors = {};
  const phone = c.phone?.trim();
  const email = c.email?.trim();
  const checkPhone = phone && !isPhoneUnchanged(c);
  if (checkPhone && phone.length !== 10) errors.phone = 'Mobile number must be 10 digits';
  else if (checkPhone && !PHONE_REGEX.test(phone)) errors.phone = 'Mobile number must start with 6–9';
  if (email && !EMAIL_REGEX.test(email)) errors.email = 'Invalid email address';
  return errors;
};

/** True when there is no usable contact, or any filled-in row is incomplete or invalid. */
export const hasInvalidContacts = (contacts) => {
  const filled = (contacts || []).filter((c) => !isBlankContact(c));
  if (filled.length === 0) return true;
  return filled.some(
    (c) =>
      !c.contactPerson?.trim() ||
      (!c.phone?.trim() && !isPhoneUnchanged(c)) ||
      Object.keys(contactErrors(c)).length > 0
  );
};

/** Request payload: drops untouched rows and trims the rest. */
export const cleanContacts = (contacts) =>
  (contacts || [])
    .filter((c) => !isBlankContact(c))
    .map((c) => ({
      contactPerson: c.contactPerson.trim(),
      email: c.email?.trim() || null,
      phone: c.phone?.trim() || null,
    }));

/** Text of every contact, for list search boxes. */
export const contactSearchText = (record, legacyPerson, legacyPhone) =>
  [
    legacyPerson,
    legacyPhone,
    ...(record.contacts || []).flatMap((c) => [c.contactPerson, c.email, c.phone]),
  ]
    .filter(Boolean)
    .join(' ');

/** Compact read-only view for list tables: primary contact, plus "+N more" with the rest on hover. */
export const ContactSummary = ({ contacts, legacyPerson, legacyPhone }) => {
  const list = contacts?.length
    ? contacts
    : legacyPerson || legacyPhone
      ? [{ contactPerson: legacyPerson, phone: legacyPhone }]
      : [];
  if (list.length === 0) return '—';

  const [primary, ...others] = list;
  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>{primary.contactPerson || '—'}</Typography>
      {primary.phone && (
        <Typography variant="caption" color="text.secondary" display="block">{primary.phone}</Typography>
      )}
      {primary.email && (
        <Typography variant="caption" color="text.secondary" display="block">{primary.email}</Typography>
      )}
      {others.length > 0 && (
        <Tooltip
          title={
            <Box>
              {others.map((c, i) => (
                <div key={i}>{[c.contactPerson, c.phone, c.email].filter(Boolean).join(' · ')}</div>
              ))}
            </Box>
          }
        >
          <Chip size="small" label={`+${others.length} more`} sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
        </Tooltip>
      )}
    </Box>
  );
};

const ContactDetailsFields = ({ contacts, onChange, disabled = false }) => {
  const rows = contacts?.length ? contacts : [{ ...EMPTY_CONTACT }];

  const updateRow = (index, field, value) =>
    onChange(rows.map((c, i) => (i === index ? { ...c, [field]: value } : c)));

  const addRow = () => onChange([...rows, { ...EMPTY_CONTACT }]);

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Contact Details
      </Typography>

      {rows.map((contact, index) => {
        const errors = contactErrors(contact);
        return (
          <Paper
            key={index}
            variant="outlined"
            sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: index === 0 ? '#f8fafc' : 'background.paper' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 32 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {index === 0 ? 'Primary Contact' : `Contact ${index + 1}`}
              </Typography>
              {rows.length > 1 && (
                <Tooltip title="Remove contact">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      aria-label={`Remove contact ${index + 1}`}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              }}
            >
              <TextField
                size="small"
                label="Contact Person"
                value={contact.contactPerson}
                onChange={(e) => updateRow(index, 'contactPerson', e.target.value)}
                required
                disabled={disabled}
              />
              <TextField
                size="small"
                label="Email"
                type="email"
                value={contact.email}
                onChange={(e) => updateRow(index, 'email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                disabled={disabled}
              />
              <TextField
                size="small"
                label="Phone"
                value={contact.phone}
                onChange={(e) => {
                  const next = e.target.value;
                  // Digits only — but let a saved "+91 ..." / landline number be deleted down
                  if (/^\d*$/.test(next) || next.length < (contact.phone || '').length) {
                    updateRow(index, 'phone', next);
                  }
                }}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                error={!!errors.phone}
                helperText={errors.phone}
                required
                disabled={disabled}
              />
            </Box>
          </Paper>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={disabled}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        Add another contact
      </Button>
    </Box>
  );
};

export default ContactDetailsFields;
